import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { buttonVariants } from './buttonVariants'

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render som barnets element (fx `<Link>`) i stedet for `<button>`. */
  asChild?: boolean
  /** Viser spinner og deaktiverer knappen. */
  isLoading?: boolean
  /** Tekst til skærmlæsere mens der indlæses. Standard: "Indlæser". */
  loadingLabel?: string
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  loadingLabel = 'Indlæser',
  leadingIcon,
  trailingIcon,
  disabled,
  children,
  ...props
}: ButtonProps) {
  // `asChild` og loading-spinner kan ikke kombineres: Slot kræver præcis ét
  // barn, og vi ville skulle indsætte to. Kalderen får knappen uden spinner.
  if (asChild) {
    return (
      <Slot className={cn(buttonVariants({ variant, size }), className)} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" />
          <span className="sr-only">{loadingLabel}</span>
        </>
      ) : (
        leadingIcon
      )}
      {children}
      {!isLoading && trailingIcon}
    </button>
  )
}

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'size'> {
  /** Påkrævet: ikonknapper har ingen synlig tekst og skal have et tilgængeligt navn. */
  label: string
  icon: ReactNode
  size?: 'icon' | 'icon-sm'
}

/**
 * Knap med kun et ikon. `label` bliver til `aria-label`, så knappen aldrig
 * ender navnløs for en skærmlæser.
 */
export function IconButton({ label, icon, size = 'icon', ...props }: IconButtonProps) {
  return (
    <Button aria-label={label} title={label} size={size} variant="ghost" {...props}>
      {icon}
    </Button>
  )
}
